import { type ComponentProps, useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { Plus, Search } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { Button, Input, Tabs, Typography, type TabItem } from '../ui';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { contactPath, useWholeContactBook } from '../../../lib/contact-book';
import { defaultTab, type OutreachTab } from '../../../lib/contact-rows';
import { outreachUrlQuery, readOutreachUrlState, sameQuery } from '../../../lib/outreach-url-state';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import { useMobileActionBarHeight } from '../../../lib/use-mobile-action-bar-height';
import { useOutreachCallTabEnabled } from '../../../lib/outreach-call-tab-flag';
import { useTranslation } from '../../../lib/i18n';
import { ContactsBody, type ContactsListState } from './ContactsBody';
import { useProfileLinkLeadRows } from '../profile-links/ProfileLinkLeadsSection';
import { InboxBody } from './InboxBody';
import { CallsBody } from './CallsBody';
import { CategoryHeader, CategoryPage, CategoryToolbar } from '../category-view/CategoryView';
import styles from './contacts.module.css';
import { useCategoryRedesign } from '../../../lib/use-category-redesign';
import { OutreachBody as OutreachBodyLegacy } from './OutreachBody.legacy';
import { TabConnectHint } from '../category-view/TabConnectHint';

/**
 * The **Outreach** screen — two peer tabs, **Contacts** (the list) and **Inbox** (placeholder for a
 * later piece). Reached from the Your Agents "Outreach" tile; the route stays `/contacts` so the
 * deeplink and drill-in pages are unchanged. Shared 1:1 with iOS/Android `OutreachScreen`.
 *
 * The search + "Add a new contact" sit per-viewport, matching each Figma frame: **desktop** puts them
 * in the toolbar beside the tabs (web `17937:268706`); **mobile-web** drops the search full-width into
 * the list and pins "Add a new contact" at the foot over a gradient scrim, like the native apps
 * (`17937:264687`). The map-home Outreach section embeds `ContactsBody` directly (no tabs).
 *
 * The Contacts tab lists the WHOLE people book under the outreach-derived head (`wholeBook`). The
 * screen opens on the Inbox when there is correspondence to show and on Contacts when there is none
 * (`defaultTab`) — decided once, on the first real data, and never over a tab the reader picked.
 *
 * A third tab, **Calls** (`CallsBody`), follows Inbox behind `symphonyOutreachCallTab`: Alfred's Connect, or its
 * number once connected. It is never the default, and it carries no search and no "Add a new contact".
 */
/** `onHome`: hosted by the home's Outreach category — no page header, no add button of its own (the
 *  home hands its own in as `action`), and the tab and searches stay out of the URL, which belongs to
 *  the home. */
function OutreachBodyRedesign({ onHome, action }: { onHome?: boolean; action?: ReactNode } = {}) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const narrow = useNarrow();
  const { logPageClickBi } = useGenericBi();
  const [tab, setTab] = useState<OutreachTab>('contacts');
  // Two independent queries — one per tab. A single shared query leaked a Contacts search into the
  // Inbox (and back) on a tab switch; iOS models the same two fields (`search` / `inboxSearch`), and
  // each tab's search-started BI hook watches its OWN query.
  const [search, setSearch] = useState('');
  const [inboxSearch, setInboxSearch] = useState('');
  const tabPicked = useRef(false);
  const tabDecided = useRef(false);

  // The tab and both searches live in the URL (`lib/outreach-url-state.ts`), because opening a contact
  // is a page navigation that unmounts this body: without it, back from a contact opened on the
  // Contacts tab landed on the Inbox. Restored ONCE, when the router has the query — and declared
  // before the default-tab effect, so a restored tab is already decided by the time that rule runs.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || (!onHome && !router.isReady)) return;
    restored.current = true;
    if (onHome) return;
    const saved = readOutreachUrlState(router.query);
    if (saved.tab) {
      tabDecided.current = true;
      tabPicked.current = true;
      setTab(saved.tab);
    }
    if (saved.search) setSearch(saved.search);
    if (saved.inboxSearch) setInboxSearch(saved.inboxSearch);
  }, [router.isReady, router.query, onHome]);

  // The same read the Contacts tab renders (one cache entry, so nothing is fetched twice), held here
  // only to choose the opening tab. `ready` is the first real data — a cached head paints it at once,
  // else the first page does; a failed first read decides nothing, and the screen stays on Contacts.
  const book = useWholeContactBook();
  // "From your profile links" — read here, handed to the Contacts list, which folds a lead who is already a
  // contact into that contact's row and lists only the rest, and to the Inbox, whose rows a nameless visitor's lead
  // names ("Visitor from Wix 3.0"). Not read on the Calls tab, which draws no leads.
  const profileLeads = useProfileLinkLeadRows(tab !== 'calls');
  useEffect(() => {
    if (!restored.current || tabDecided.current || book.kind !== 'ready') return;
    tabDecided.current = true;
    if (!tabPicked.current) setTab(defaultTab(book.contacts));
  }, [book, router.isReady]);

  // …and written back as they change. `replace`, shallow, so it adds no history entry and refetches
  // nothing — the browser's back then returns to exactly this. No tab is written until one is decided,
  // so a fresh `/contacts` still gets the default rule.
  useEffect(() => {
    if (!restored.current || onHome) return;
    const next = outreachUrlQuery({ tab: tabDecided.current ? tab : undefined, search, inboxSearch }, router.query);
    if (sameQuery(router.query, next)) return;
    void router.replace({ pathname: router.pathname, query: next }, undefined, { shallow: true, scroll: false });
  }, [tab, search, inboxSearch, router, onHome]);
  const [listState, setListState] = useState<ContactsListState>('loading');
  const [filtersSlot, setFiltersSlot] = useState<HTMLElement | null>(null);

  // `undefined` until the gate answers. A `calls` tab restored from the URL waits for it, and falls back to
  // Contacts only once the gate says the tab is off.
  const callTab = useOutreachCallTabEnabled();
  useEffect(() => {
    if (callTab === false && tab === 'calls') setTab('contacts');
  }, [callTab, tab]);

  const tabs: TabItem<OutreachTab>[] = [
    { value: 'contacts', label: t('contacts.title') },
    { value: 'inbox', label: t('outreach.tab.inbox') },
    ...(callTab === true ? [{ value: 'calls' as const, label: t('outreach.tab.calls') }] : []),
  ];

  const addContact = () => logPageClickBi({ page_name: 'contacts_page', button_name: 'add_contact' });
  // "Add a new contact" drops the user into a fresh chat with a seeded first message — the same handoff
  // the contact detail's "Reach out" uses, so adding a contact is a conversation, not a form.
  const addContactHref = chatHandoffLink({ kind: 'new', message: t('contacts.addContactSeed') }, basePath).route;
  const addContactAction = (
    <Button href={addContactHref} onClick={addContact}>
      {t('contacts.addContact')}
    </Button>
  );

  const onContacts = tab === 'contacts';
  // A `calls` tab (restored from the URL) renders only once the gate says yes: while it is unanswered the body
  // is empty, so a closed toggle never mounts the Calls tab or its reads, even for a moment.
  const onCalls = tab === 'calls' && callTab === true;
  const callsPending = tab === 'calls' && callTab !== true;
  // Desktop toolbar search + Add show on both LIST tabs once the active list is populated (loading and the
  // illustrated empty state carry their own chrome — a spinner, or the in-card CTA). Calls has neither.
  const showToolbarActions = !onCalls && !callsPending && !narrow && listState !== 'loading' && listState !== 'empty';
  const showToolbarAdd = showToolbarActions && !onHome;
  // Mobile-web pins the foot CTA on both list tabs: Contacts in every state, and the Inbox empty state (its
  // in-card CTA is desktop-only, matching the Contacts empty card). Not on Calls.
  const showFootCta = narrow && !onCalls && !callsPending && !onHome;
  // The chat bar rides above the pinned button rather than covering it.
  const footCtaRef = useRef<HTMLDivElement>(null);
  useMobileActionBarHeight(footCtaRef, showFootCta);
  // Nothing while a restored `calls` waits on the gate: the Calls copy must not show to someone outside it.
  const subtitle = callsPending ? null : onCalls ? t('outreach.calls.subtitle') : onContacts ? t('contacts.description') : t('outreach.inbox.subtitle');

  return (
    <div className={`${styles.body} ${styles.outreachRoot}`}>
      {!onHome && (
        <PageHeader
          title={t('home.map.outreach')}
          breadcrumb={[{ label: t('nav.yourAgents'), href: '/agents' }, { label: t('home.map.outreach') }]}
        />
      )}

      <CategoryPage>
        {/* No Space filter: contacts and their threads carry no workspace id, so there is nothing to
            narrow by (the same reason both phones draw none). */}
        {onHome && <CategoryHeader title={t('categoryView.outreach.title')} description={<TabConnectHint tab="outreach" fallback={t('categoryView.outreach.description')} />} />}
        <CategoryToolbar
          end={
            showToolbarActions ? (
              <>
              {/* The Inbox's Channel · Contact · Status land here (Figma 21055:254883). */}
              {!onContacts && <span ref={setFiltersSlot} className={styles.toolbarFilters} />}
              <Input
                size="medium"
                type="search"
                startIcon={<Search size={16} aria-hidden />}
                // The toolbar draws ONE field but edits the ACTIVE tab's own query, so switching tabs
                // shows that tab's search rather than carrying the other's over.
                value={onContacts ? search : inboxSearch}
                onChange={(event) => (onContacts ? setSearch : setInboxSearch)(event.target.value)}
                onClear={() => (onContacts ? setSearch : setInboxSearch)('')}
                clearAriaLabel={t('search.dialog.clear')}
                placeholder={onContacts ? t('contacts.search.placeholder') : t('outreach.inbox.search.placeholder')}
                className={styles.toolbarSearch}
              />
              </>
            ) : null
          }
          action={onHome ? action : showToolbarAdd ? addContactAction : null}
        >
          <Tabs<OutreachTab>
            variant="category"
            items={tabs}
            value={tab}
            onChange={(value) => {
              logPageClickBi({ page_name: 'contacts_page', button_name: `tab_${value}` });
              // The reader has chosen; the default rule never overrides that — and a pick is a decision,
              // so it is written to the URL even if it beat the default rule to the first read.
              tabPicked.current = true;
              tabDecided.current = true;
              setTab(value);
            }}
            label={t('home.map.outreach')}
          />
        </CategoryToolbar>

      {/* The tab's one-line explainer is the phones' (Figma 21055:255008); the desktop frame has none. */}
      {subtitle !== null && narrow ? (
        <Typography variant="p2" color="secondary" className={styles.outreachSubtitle}>
          {subtitle}
        </Typography>
      ) : null}

      {callsPending ? null : onCalls ? (
        <CallsBody />
      ) : onContacts ? (
        <ContactsBody
          onOpen={(id) => router.push(contactPath(id))}
          // Lead cards from the owner's profile links (spec-gated; `[]` off or empty). The body matches them to
          // the contacts it lists and draws the section for the unmatched rest (not under a search).
          profileLeads={profileLeads}
          illustratedEmpty
          // Desktop: the CTA lives inside the empty card. Narrow: it is pinned at the foot instead.
          emptyAction={narrow ? undefined : addContactAction}
          onListState={setListState}
          search={search}
          onSearchChange={setSearch}
          // Desktop draws the search in the toolbar; narrow lets the body draw it full-width.
          renderSearch={narrow}
          wholeBook
        />
      ) : (
        <InboxBody
          onOpen={(id) => router.push(contactPath(id))}
          emptyAction={narrow ? undefined : addContactAction}
          onListState={setListState}
          search={inboxSearch}
          onSearchChange={setInboxSearch}
          renderSearch={narrow}
          filtersSlot={narrow ? undefined : filtersSlot}
          profileLeads={profileLeads}
        />
      )}

      </CategoryPage>

      {/* Native-style foot CTA on mobile-web: a full-width button pinned to the bottom over a
          transparent→canvas gradient, the same treatment the Packs/Goals sticky CTAs use. */}
      {showFootCta ? (
        <div ref={footCtaRef} className={styles.footCta}>
          <Button fullWidth href={addContactHref} startIcon={<Plus size={16} aria-hidden />} onClick={addContact}>
            {t('contacts.addContact')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** The redesign rides the activity-map home's release (`lib/use-category-redesign.ts`); off, this is the legacy design. */
export function OutreachBody(props: ComponentProps<typeof OutreachBodyRedesign>) {
  const redesign = useCategoryRedesign();
  if (!redesign.resolved) return null;
  return redesign.on ? <OutreachBodyRedesign {...props} /> : <OutreachBodyLegacy {...props} />;
}
